import React, { useEffect, useRef, useState } from "react";
import { City, TrackIds } from "../../types/types";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/useLanguage";
import { BusinessCatalog } from "../../types/requestsOutputs";
import {
    addTrackBusinessCatalog,
    getCitiesAdmin,
    getTrackIds,
    removeTrackBusinessCatalog,
    updateBusinessCatalog,
} from "../../api/administration";
import NumberInput from "../common/NumberInput";
import { ModalPortal } from "./ModalPortal";
import styles from "./CompanyEditModal.module.css";

interface BusinessCatalogEditModalProps {
    businessCatalog: BusinessCatalog;
    isOpen: boolean;
    onClose: () => void;
}

const BusinessCatalogEditModal: React.FC<BusinessCatalogEditModalProps> = ({ businessCatalog, isOpen, onClose }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [activeTab, setActiveTab] = useState<"tracks" | "cities" | "info">("info");
    const [, setLoading] = useState<boolean>(true);
    const { language } = useLanguage();
    const [, setCities] = useState<City[]>([]);
    const [tracks, setTracks] = useState<TrackIds[]>([]);
    const [selectedBusinessCatalog, setSelectedBusinessCatalog] = useState<BusinessCatalog>(businessCatalog);

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedBusinessCatalog(businessCatalog); // reset à chaque ouverture
            fetchCities();
            fetchTrackIds();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, businessCatalog, language]);

    const fetchCities = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getCitiesAdmin(token, language);
            setCities(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTrackIds = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getTrackIds(token);
            setTracks(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const isTrackAssociated = (trackId: string) => {
        return selectedBusinessCatalog.tracks?.some((trackObj) => trackObj.id === trackId);
    };

    const handleAddTrack = (trackId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        addTrackBusinessCatalog(token, selectedBusinessCatalog.id, trackId);

        const track = tracks.find((t) => t.id === trackId);
        if (!track) {
            console.error("Track not found");
            return;
        }
        setSelectedBusinessCatalog((prev) => ({
            ...prev,
            tracks: [...(prev.tracks || []), track],
        }));
    };

    const handleRemoveTrack = (trackId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        removeTrackBusinessCatalog(token, selectedBusinessCatalog.id, trackId);
        setSelectedBusinessCatalog((prev) => ({
            ...prev,
            tracks: prev.tracks?.filter((track) => track.id !== trackId) || [],
        }));
    };

    if (!isOpen) return null;

    return (
        <ModalPortal>
            <div className={styles.overlay}>
                <div className={styles.content}>
                    <h2>
                        {t("editBusinessCatalog")} - {selectedBusinessCatalog.internalName}
                    </h2>

                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab}${activeTab === "tracks" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("tracks")}
                        >
                            {t("tracks")}
                        </button>

                        <button
                            className={`${styles.tab}${activeTab === "info" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("info")}
                        >
                            {t("info")}
                        </button>
                    </div>

                    {activeTab === "tracks" && (
                        <div className={styles.tabContent}>
                            <div className={styles.citiesManagement}>
                                <div className={styles.citiesColumns}>
                                    {/* Tracks associées */}
                                    <div className={styles.citiesColumn}>
                                        <h3>{t("associatedTracks")}</h3>
                                        <ul>
                                            {tracks
                                                .sort((a, b) =>
                                                    a?.name && b?.name ? a.name.localeCompare(b?.name) : 0,
                                                )
                                                .filter((track) => isTrackAssociated(track.id))
                                                .map((track) => (
                                                    <li key={track.id}>
                                                        #{track.id}
                                                        {" - "}
                                                        {track.name}
                                                        <button
                                                            className={styles.removeBtn}
                                                            onClick={() => handleRemoveTrack(track.id)}
                                                            style={{ marginLeft: "20px" }}
                                                        >
                                                            {t("unlink")}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>

                                    {/* Tracks non associées */}
                                    <div className={styles.citiesColumn}>
                                        <h3>{t("availableTracks")}</h3>
                                        <ul>
                                            {tracks
                                                .sort((a, b) =>
                                                    a?.name && b?.name ? a.name.localeCompare(b?.name) : 0,
                                                )
                                                .filter((track) => !isTrackAssociated(track.id))
                                                .map((track) => (
                                                    <li key={track.id}>
                                                        #{track.id}
                                                        {" - "}
                                                        {track.name}
                                                        <button
                                                            className={styles.addBtn}
                                                            onClick={() => handleAddTrack(track.id)}
                                                            style={{ marginLeft: "20px" }}
                                                        >
                                                            {t("link")}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "info" && (
                        <div>
                            <h4>{t("numberTracks")}</h4>
                            <NumberInput
                                min={1}
                                placeholder={t("selectNumber")}
                                value={selectedBusinessCatalog.nbrTracks}
                                size="medium"
                                onChange={(e) => {
                                    const value = parseInt(e.target.value || "0", 10);
                                    // Ignore NaN or negative values
                                    if (isNaN(value) || value <= 0) return;

                                    setSelectedBusinessCatalog((prev) => ({
                                        ...prev,
                                        nbrTracks: value,
                                    }));

                                    if (debounceTimerRef.current) {
                                        clearTimeout(debounceTimerRef.current);
                                    }

                                    debounceTimerRef.current = setTimeout(() => {
                                        const token = localStorage.getItem("token");
                                        if (!token) {
                                            console.error("Token not found");
                                            return;
                                        }

                                        updateBusinessCatalog(token, businessCatalog.id, {
                                            nbrTracks: value,
                                        });
                                    }, 400);
                                }}
                            />
                        </div>
                    )}
                    <div className={styles.footer}>
                        <button className={styles.buttonCancel} onClick={onClose}>
                            {t("close")}
                        </button>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default BusinessCatalogEditModal;
